import React from 'react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <section className="privacy-policy">
      <div className="privacy-content">
        <h1>Repo Prompt Privacy Policy</h1>
        
        <p>
          <strong>Effective Date:</strong> April 12, 2025<br />
          <strong>Last Updated:</strong> April 12, 2025
        </p>
        
        <p>
          Repo Prompt ("<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>") is committed to protecting your privacy.
          This Privacy Policy explains in clear terms how Repo Prompt collects, uses, and safeguards information when you use our application.
          It is designed to comply with the European Union's General Data Protection Regulation (GDPR) and Quebec's Law 25 (Bill 25),
          as well as other applicable global privacy laws, ensuring transparency and legal adequacy for all our users worldwide.
        </p>

        <hr />

        <div className="policy-section">
          <h2>1. Scope and Application</h2>
          <p>
            This Privacy Policy applies to the Repo Prompt application and any related services. By using Repo Prompt,
            you acknowledge that you have read and understood this Policy. This Policy covers users globally, including residents
            of the European Economic Area (EEA) and Quebec, Canada, and addresses the specific requirements of GDPR and Quebec's Law 25.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>2. Who We Are</h2>
          <p>
            Repo Prompt is a locally-running application designed to assist with repository analysis and prompts. The application operates
            primarily on your device. There are no user accounts or registration with Repo Prompt, and we do not directly collect personal
            identifiers such as your name or email. For the purposes of data protection laws, the <strong>data controller</strong> of any
            personal data (if any) collected by Repo Prompt is:
          </p>
          <p>
            <strong>Eric Provencher</strong><br />
            (Founder and Privacy Officer)<br />
            Email: contact@repoprompt.com
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>3. Information We Collect</h2>
          <p>
            We do <strong>not</strong> collect any personal information that can identify you. Repo Prompt only collects a minimal
            amount of anonymous usage data for analytics and performance monitoring, as described below:
          </p>
          <ul>
            <li>
              <strong>Anonymous Usage Analytics:</strong> We use PostHog to gather usage statistics and understand how Repo Prompt is used.
              This includes basic, non-identifying information about feature usage and event counts.
              <p>
                We have explicitly configured PostHog to <strong>exclude</strong> the following properties:
              </p>
              <ul>
                <li>IP address</li>
                <li>Operating system and device type</li>
                <li>Geographic location (latitude, longitude, city, country, postal code)</li>
                <li>Time zone and regional subdivision data</li>
              </ul>
              <p>
                No personally identifying information (PII) is collected, and all analytics data is either <strong>aggregated</strong> or <strong>de-identified</strong>.
              </p>
              <p>
                PostHog data is stored in the United States and processed under GDPR-compliant terms, including Data Processing Agreements
                and standard contractual clauses.
              </p>
            </li>
            <li>
              <strong>Crash Reports and Diagnostics:</strong> We use Sentry for crash reporting and diagnostics. These reports may include
              technical information such as error messages, stack traces, and non-identifying device metadata (e.g., operating system or browser type).
              <p>
                We have configured Sentry to:
              </p>
              <ul>
                <li>Scrub or remove IP addresses by default</li>
                <li>Avoid collection of custom events or user identifiers</li>
                <li>Exclude sensitive or personally identifiable information (PII) from logs and reports</li>
              </ul>
              <p>
                While Sentry may automatically capture general device and environment data (e.g., OS version, platform), this information is not
                linked to any user identity and cannot be used to identify individuals. We also do not use session replay, breadcrumb tracking,
                query parameters, or console logs.
              </p>
              <p>
                All crash data is processed under GDPR-compliant data processing terms. We rely on Sentry's participation in the EU-U.S. Data Privacy
                Framework and Standard Contractual Clauses to ensure adequate protection for data stored in the United States.
              </p>
            </li>
          </ul>
          <p>
            <strong>We do not collect or store:</strong>
          </p>
          <ul>
            <li>Your name, email, or contact information</li>
            <li>IP addresses or device identifiers</li>
            <li>The contents of your code or files</li>
            <li>Cookies or session data</li>
          </ul>
        </div>

        <hr />

        <div className="policy-section">
          <h2>4. How We Use the Collected Data</h2>
          <p>
            We use the limited data collected <strong>only</strong> to:
          </p>
          <ul>
            <li>Improve application performance and feature design</li>
            <li>Identify and fix crashes or bugs</li>
          </ul>
          <p>
            We do <strong>not</strong> use data for marketing, profiling, or advertising, and we never sell or share data for commercial purposes.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>5. Legal Basis for Processing</h2>
          <p>
            Under GDPR, we process data on the basis of <strong>legitimate interest</strong>—specifically, to ensure the stability and functionality of the app.
            Under Quebec's Law 25, we consider the anonymized nature of the data and user installation as valid consent.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>6. Where and How We Store Data</h2>
          <p>
            Analytics and crash data are transmitted and stored by our third-party service providers:
          </p>
          <ul>
            <li><strong>PostHog (Analytics)</strong> — stored in the <strong>United States</strong></li>
            <li><strong>Sentry (Crash Reports)</strong> — stored in the <strong>United States</strong></li>
          </ul>
          <p>
            These services comply with GDPR and offer appropriate safeguards (e.g., Data Processing Agreements, standard contractual clauses).
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>7. Third-Party Service Providers</h2>
          <p>
            We use the following third-party services:
          </p>
          <ul>
            <li><strong>PostHog</strong> – for anonymous usage analytics</li>
            <li><strong>Sentry</strong> – for crash and performance monitoring</li>
            <li><strong>Polar</strong> – merchant of record for license purchases; handles all payment-related personal data</li>
          </ul>
          <h3>Payment Information:</h3>
          <p>
            All payment transactions are processed by our third-party provider, Polar. While we can access certain purchase details via Polar,
            we do not control this data. For privacy-related inquiries or to exercise your rights concerning payment information, please contact
            Polar directly at <strong>support@polar.sh</strong>. For more details, refer to Polar's Privacy Policy: <a href="https://polar.sh/legal/privacy" target="_blank" rel="noopener noreferrer" className="policy-link">https://polar.sh/legal/privacy</a>.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>8. Data Retention</h2>
          <p>
            We retain anonymous usage and crash data only as long as necessary for diagnostics and performance monitoring. We regularly review and
            delete or anonymize old data. No personally identifiable information is collected or stored.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>9. Your Privacy Rights</h2>
          <p>
            Repo Prompt does not collect personal identifiers or maintain user-specific data, which means we cannot link any data back to an individual user.
            As such, the following rights are limited in scope:
          </p>
          <ul>
            <li><strong>Access or Deletion Requests:</strong> We are unable to access or delete specific data associated with an individual user because we do not collect identifiable data.</li>
            <li><strong>Opt-out Options:</strong> You may opt out of analytics and crash reporting by disabling network access to these services or by modifying your device's configuration.</li>
            <li><strong>Withdraw Consent:</strong> Consent is considered granted through the act of installation and use, but since no identifiable data is collected, withdrawal has no effect on stored data.</li>
            <li><strong>Complaints:</strong> You may file a complaint with a privacy regulator in your jurisdiction if you believe your rights under applicable law are being violated.</li>
          </ul>
          <p>
            To exercise your rights, email us at: <strong>contact@repoprompt.com</strong>
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>10. Security Measures</h2>
          <p>
            We use appropriate safeguards to protect data, including HTTPS encryption and restricted access. Crash data is anonymized,
            and no code or personal data is collected. The app is built around privacy-by-design principles, minimizing any data processing.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>11. External APIs and Integrations</h2>
          <p>
            Repo Prompt may allow optional API integrations. When used:
          </p>
          <ul>
            <li>Data is sent <strong>directly</strong> from your device to the provider</li>
            <li>We do <strong>not</strong> intercept, log, or transmit API data</li>
            <li>You are responsible for reviewing third-party API privacy terms</li>
          </ul>
        </div>

        <hr />

        <div className="policy-section">
          <h2>12. Children's Privacy</h2>
          <p>
            Repo Prompt is not intended for children under 13. We do not knowingly collect information from children.
            If such data is discovered, we will delete it immediately upon request.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this policy as needed. The latest version will always be dated at the top.
            Continued use of Repo Prompt after updates constitutes acceptance of any changes.
          </p>
        </div>

        <hr />

        <div className="policy-section">
          <h2>14. Contact Us and Privacy Officer Information</h2>
          <p>
            If you have any questions, concerns, or privacy-related requests, please contact:
          </p>
          <p>
            <strong>Eric Provencher</strong><br />
            Founder and Privacy Officer<br />
            RepoPrompt<br />
            Email: contact@repoprompt.com<br />
            Mailing Address:<br />
            3484 Boul DesSources, PO Box #625<br />
            Dollard-Des-Ormeaux, QC H9B 1Z9<br />
            Canada
          </p>
        </div>
      </div>
    </section>
  );
}

export default PrivacyPolicy;